import React, { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ShieldAlert, ShieldBan, ShieldCheck, Info } from "lucide-react";
import { ScreeningVerdictEnum } from "@forgepoint/shared";
import type {
  AgentSdkMessage,
  InboundScreeningResult,
  ScreeningVerdict,
} from "@forgepoint/shared";
import {
  isScreeningHitMessage,
  isUserSdkMessage,
} from "@/modules/standardAgent/components/utils";

export interface MessageReviewPanelProps {
  messages: AgentSdkMessage[];
}

/**
 * Formats a SNAKE_CASE string to Title Case.
 * @param value - The SNAKE_CASE string to format
 * @returns The formatted Title Case string
 */
const formatSnakeCaseToTitleCase = (value: string): string => {
  return value
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Returns styling for a verdict badge.
 * @param verdict - The screening verdict
 * @returns Object with className and variant for the Badge component
 */
const getVerdictBadgeProps = (
  verdict: ScreeningVerdict,
): { className: string | undefined; variant: "destructive" | "default" } => {
  if (verdict === ScreeningVerdictEnum.BLOCK) {
    return { className: undefined, variant: "destructive" };
  }
  if (verdict === ScreeningVerdictEnum.FLAG) {
    return {
      className:
        "bg-amber-500/15 text-amber-600 border-amber-500/30 hover:bg-amber-500/20",
      variant: "default",
    };
  }
  return {
    className:
      "bg-green-500/15 text-green-600 border-green-500/30 hover:bg-green-500/20",
    variant: "default",
  };
};

/**
 * Returns the icon component for a screening verdict.
 * @param verdict - The screening verdict
 * @returns The icon component
 */
const getVerdictIcon = (verdict: string) => {
  if (verdict === ScreeningVerdictEnum.BLOCK) {
    return ShieldBan;
  }
  if (verdict === ScreeningVerdictEnum.FLAG) {
    return ShieldAlert;
  }
  return ShieldCheck;
};

/**
 * Renders a single screening message card from message metadata.
 * @param props - The component props
 * @returns The rendered message card
 */
const ScreeningMessageCard: React.FC<{
  message: AgentSdkMessage;
  screeningResult: InboundScreeningResult;
}> = ({ message, screeningResult }) => {
  const badgeProps = getVerdictBadgeProps(screeningResult.verdict);
  const sender =
    message.messageMetadata?.senderMetadata?.email ??
    message.messageMetadata?.senderMetadata?.phoneNumber;
  const Icon = getVerdictIcon(screeningResult.verdict);
  const content =
    message.message.type === "message" &&
    typeof message.message.content === "string"
      ? message.message.content
      : "";

  return (
    <div className="rounded-md border p-4 bg-muted">
      <div className="flex items-center gap-2 mb-2">
        <Icon
          className={`h-4 w-4 ${
            screeningResult.verdict === ScreeningVerdictEnum.BLOCK
              ? "text-destructive"
              : screeningResult.verdict === ScreeningVerdictEnum.FLAG
                ? "text-amber-500"
                : "text-green-500"
          }`}
        />
        <Badge className={badgeProps.className} variant={badgeProps.variant}>
          {screeningResult.verdict}
        </Badge>
        {screeningResult.category && (
          <Badge variant="outline" className="text-xs">
            {formatSnakeCaseToTitleCase(screeningResult.category)}
          </Badge>
        )}
        <span className="text-xs text-muted-foreground ml-auto">
          {new Date(message.createdAt).toLocaleString()}
        </span>
      </div>
      {sender && (
        <p className="text-xs text-muted-foreground mb-1">
          <span className="font-medium text-foreground">From:</span> {sender}
        </p>
      )}
      {content && <p className="text-sm whitespace-pre-wrap mt-1">{content}</p>}
      {screeningResult.reason && (
        <p className="text-xs text-muted-foreground mt-2 italic">
          {screeningResult.reason}
        </p>
      )}
    </div>
  );
};

/**
 * Content-only panel for reviewing screened messages (both BLOCK and FLAG verdicts).
 * Rendered inside RichActionPanel's shell — does not include its own header or chrome.
 * @param props - The component props
 * @returns The rendered screening content
 */
export const MessageReviewPanel: React.FC<MessageReviewPanelProps> = ({
  messages,
}) => {
  const screeningMessages = useMemo(() => {
    const firstHitIndex = messages.findIndex(isScreeningHitMessage);
    if (firstHitIndex === -1) {
      return [];
    }
    return messages.filter(
      (msg, idx) => idx >= firstHitIndex && isUserSdkMessage(msg),
    );
  }, [messages]);

  const hasMultipleMessages = screeningMessages.length > 1;

  const primaryScreening =
    screeningMessages[0]?.messageMetadata?.screeningResult;
  const isBlock = primaryScreening?.verdict === ScreeningVerdictEnum.BLOCK;
  const Icon = isBlock ? ShieldBan : ShieldAlert;

  return (
    <div className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon
          className={`h-5 w-5 ${isBlock ? "text-destructive" : "text-amber-500"}`}
        />
        <h2 className="text-lg font-semibold">
          {isBlock ? "Blocked External Message" : "Flagged External Message"}
          {hasMultipleMessages && (
            <span className="text-sm font-normal text-muted-foreground ml-2">
              ({screeningMessages.length} messages)
            </span>
          )}
        </h2>
      </div>

      {hasMultipleMessages ? (
        <div className="space-y-3">
          {screeningMessages.map((msg) => {
            const result = msg.messageMetadata?.screeningResult;
            if (!result) {
              return null;
            }
            return (
              <ScreeningMessageCard
                key={msg.id}
                message={msg}
                screeningResult={result}
              />
            );
          })}
        </div>
      ) : (
        <>
          {primaryScreening && (
            <>
              {(() => {
                const firstMsg = screeningMessages[0];
                const sender =
                  firstMsg?.messageMetadata?.senderMetadata?.email ??
                  firstMsg?.messageMetadata?.senderMetadata?.phoneNumber;
                const content =
                  firstMsg?.message.type === "message" &&
                  typeof firstMsg.message.content === "string"
                    ? firstMsg.message.content
                    : "";

                return (
                  <>
                    {sender && (
                      <p className="text-sm text-muted-foreground mb-3">
                        <span className="font-medium text-foreground">
                          From:
                        </span>{" "}
                        {sender}
                      </p>
                    )}

                    {content && (
                      <p className="text-sm whitespace-pre-wrap mb-3">
                        {content}
                      </p>
                    )}
                  </>
                );
              })()}

              <Separator className="my-4" />

              <div className="rounded-md border p-4 bg-muted">
                {primaryScreening.category && (
                  <div className="mb-2">
                    <Badge
                      className={
                        isBlock
                          ? undefined
                          : "bg-amber-500/15 text-amber-600 border-amber-500/30 hover:bg-amber-500/20"
                      }
                      variant={isBlock ? "destructive" : "default"}
                    >
                      {formatSnakeCaseToTitleCase(primaryScreening.category)}
                    </Badge>
                  </div>
                )}

                {primaryScreening.reason && (
                  <p className="text-sm text-muted-foreground">
                    {primaryScreening.reason}
                  </p>
                )}
              </div>
            </>
          )}
        </>
      )}

      <div className="flex items-start gap-2 mt-4">
        <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground">
          {isBlock
            ? "This message was identified as likely malicious — it may contain prompt injection or other harmful content. Proceed with extreme caution."
            : "This message is suspicious but may be legitimate. Review the content and approve if appropriate."}
        </p>
      </div>
    </div>
  );
};
